// The agency's account like a bank account (design 5): the balance and its
// curve, the day it reaches zero, the date to top up before, the week's
// numbers and the statement. The agency's « Compte » page, and MyGaz's
// « Voir le compte », which adds the margin before the pause, the terms, the
// week and the adjustment (D-43 keeps the overdraft off every agency screen).
// Reworked after Ruben's review on 2 Nov 2026: nothing drawn outside the
// chart, no empty forecast space, a headline and three facts instead of a
// paragraph, one main column and one side column so no gap is left.
import { useId, useState } from 'react';
import { S } from '../../lib/strings';
import { dayLabel, hhmm, niceScale, xaf } from '../../lib/format';
import { dayShort, hm, longDay, netName, runwayWords, signedXaf, smoothPath, statementCsv } from '../../lib/agences';
import type { Statement } from '../../lib/types';
import { Ic } from '../Icon';
import { AccountPill, MiniBars, Net, last7 } from './Bits';

export interface AccountSummary {
  name: string; balance: number; fee_pct: number; fee_per_day: number; days_to_zero: number | null; paused: boolean;
  below_zero_since: string | null; topup_before: string | null;
}

/** How far ahead the chart may look: a forecast further than this is said in words, not drawn. */
const HORIZON = 6;
const daysUntil = (iso: string | null) => (iso ? (new Date(iso).getTime() - Date.now()) / 86400000 : null);

// The balance over 14 days (dataviz rules: one 2px line, a 10 % wash under it,
// red below zero, hairline grid, the end labelled, a crosshair and a readout on
// hover or with the arrow keys). Top-ups are dots in their network's colour.
function Curve({ st, acc, limit }: { st: Statement; acc: AccountSummary; limit?: number }) {
  const [hov, setHov] = useState<number | null>(null);
  const uid = useId().replace(/:/g, '');
  const W = 860, H = 280, pl = 44, pr = 16, pt = 30, pb = 28;
  const n = st.days.length;
  const soon = (d: number | null) => (d != null && d >= 0 && d <= HORIZON ? d : null);
  const zeroD = soon(acc.balance > 0 && acc.fee_per_day > 0 ? acc.balance / acc.fee_per_day : null);
  const pauseD = soon(limit != null && acc.fee_per_day > 0 && acc.balance > -limit ? (acc.balance + limit) / acc.fee_per_day : null);
  const beforeD = limit == null ? soon(daysUntil(acc.topup_before)) : null;
  const ahead = Math.max(zeroD ?? 0, pauseD ?? 0, beforeD ?? 0);
  // Room on the right only for what will happen within the horizon.
  const span = (n - 1) + (ahead > 0 ? Math.min(HORIZON, ahead) + 0.6 : 0.6);
  const X = (d: number) => pl + (d / span) * (W - pl - pr);
  const vals = st.days.map((d) => d.balance).concat(acc.balance, 0, limit != null ? -limit : 0);
  const top = Math.max(...vals), bottom = Math.min(...vals);
  const hi = niceScale(Math.max(1, top) * 1.12, 4).mx, lo = bottom < 0 ? -niceScale(-bottom * 1.2, 2).mx : 0;
  const Y = (v: number) => pt + ((hi - v) / (hi - lo || 1)) * (H - pt - pb);
  const pts: [number, number][] = st.days.map((d, i) => [X(i), Y(i === n - 1 ? acc.balance : d.balance)]);
  const line = smoothPath(pts);
  const area = `${line} L${pts[n - 1][0]},${Y(0)} L${pts[0][0]},${Y(0)} Z`;
  const ticks = niceScale(hi, 4).ticks.filter((t) => t <= hi && t > 0);
  const nowX = X(n - 1), nowY = Y(acc.balance);
  const at = (d: number) => acc.balance - d * acc.fee_per_day;
  const fmtK = (v: number) => (v === 0 ? '0' : `${(v / 1000).toLocaleString('fr-FR')} k`);
  const day = (i: number) => dayLabel(st.days[i].day + 'T12:00:00+01:00', { weekday: 'short', day: 'numeric', month: 'short' });
  const fee = (i: number) => (i > 0 ? Math.max(0, st.days[i - 1].balance + st.days[i].topup - st.days[i].balance) : null);
  const pick = (clientX: number, el: SVGSVGElement) => {
    const r = el.getBoundingClientRect(), x = ((clientX - r.left) / r.width) * W;
    setHov(Math.max(0, Math.min(n - 1, Math.round(((x - pl) / (W - pl - pr)) * span))));
  };
  const h = hov != null ? st.days[hov] : null;
  const nearEdge = W - pr - nowX < 70;
  return (
    <div className="ag-cv">
      <svg className="ag-curve" viewBox={`0 0 ${W} ${H}`} width="100%" role="img" tabIndex={0}
        aria-label={`${S.agency.st.title} · ${signedXaf(acc.balance)}`}
        onPointerMove={(e) => pick(e.clientX, e.currentTarget)} onPointerLeave={() => setHov(null)} onBlur={() => setHov(null)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') { e.preventDefault(); setHov((i) => Math.max(0, Math.min(n - 1, (i ?? n - 1) + (e.key === 'ArrowLeft' ? -1 : 1)))); }
          if (e.key === 'Escape') setHov(null);
        }}>
        <defs>
          <linearGradient id={`up${uid}`} x1="0" x2="0" y1="0" y2="1"><stop offset="0" className="ag-g-up" stopOpacity=".18" /><stop offset="1" className="ag-g-up" stopOpacity="0" /></linearGradient>
          <clipPath id={`p${uid}`}><rect x={pl} y="0" width={W - pl - pr + 1} height={H - pb + 6} /></clipPath>
          <clipPath id={`a${uid}`}><rect x={pl} y="0" width={W - pl - pr + 1} height={Y(0) + 1.5} /></clipPath>
          <clipPath id={`b${uid}`}><rect x={pl} y={Y(0) + 1.5} width={W - pl - pr + 1} height={H - Y(0)} /></clipPath>
        </defs>
        {ticks.map((t) => <g key={t}><line x1={pl} x2={W - pr} y1={Y(t)} y2={Y(t)} className="grid" /><text x={pl - 10} y={Y(t) + 4} textAnchor="end" className="ax">{fmtK(t)}</text></g>)}
        <line x1={pl} x2={W - pr} y1={Y(0)} y2={Y(0)} className="zero" /><text x={pl - 10} y={Y(0) + 4} textAnchor="end" className="ax">0</text>
        {lo < 0 && <text x={pl - 10} y={Y(lo) + 4} textAnchor="end" className="ax">{fmtK(lo)}</text>}
        {[0, Math.floor((n - 1) / 2), n - 1].map((i) => <text key={i} x={X(i)} y={H - 6} textAnchor={i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle'} className="ax">{i === n - 1 ? S.agency.st.today : day(i)}</text>)}
        {limit != null && <><line x1={pl} x2={W - pr} y1={Y(-limit)} y2={Y(-limit)} className="limit" /><text x={pl + 8} y={Y(-limit) + 15} className="lim">{S.ag.acct.limitLine(xaf(limit))}</text></>}
        <path d={area} fill={`url(#up${uid})`} clipPath={`url(#a${uid})`} />
        <path d={area} className="wash-neg" clipPath={`url(#b${uid})`} />
        <path d={line} className="line" clipPath={`url(#a${uid})`} />
        <path d={line} className="line neg" clipPath={`url(#b${uid})`} />
        <g clipPath={`url(#p${uid})`}>
          {zeroD != null && <line x1={nowX} y1={nowY} x2={X(n - 1 + zeroD)} y2={Y(0)} className="fc" />}
          {pauseD != null && limit != null && <line x1={X(n - 1 + (zeroD ?? 0))} y1={Y(Math.min(0, acc.balance))} x2={X(n - 1 + pauseD)} y2={Y(at(pauseD))} className="fc neg" />}
          {pauseD != null && limit != null && <circle cx={X(n - 1 + pauseD)} cy={Y(-limit)} r="4.5" className="ppt" />}
          {zeroD != null && <><circle cx={X(n - 1 + zeroD)} cy={Y(0)} r="4.5" className="zpt" /><text x={X(n - 1 + zeroD) + 9} y={Y(0) - 9} className="zl">{S.agency.st.zeroMark}</text></>}
          {beforeD != null && (
            <><line x1={X(n - 1 + beforeD)} x2={X(n - 1 + beforeD)} y1={Y(0) - 18} y2={Y(0) + 6} className="before" />
              <text x={X(n - 1 + beforeD) - 7} y={Y(0) - 24} textAnchor="end" className="bl">{S.agency.st.beforeMark(dayLabel(acc.topup_before!, { weekday: 'short', day: 'numeric', month: 'short' }))}</text></>
          )}
        </g>
        {st.days.map((d, i) => d.topup > 0 && <circle key={d.day} cx={X(i)} cy={Y(d.balance)} r="5" className={'tp ' + ((d.rail ?? '').startsWith('Orange') ? 'org' : 'mtn')} />)}
        {hov != null && <line x1={X(hov)} x2={X(hov)} y1={pt - 10} y2={H - pb} className="cross" />}
        {hov != null && <circle cx={X(hov)} cy={pts[hov][1]} r="5" className="hovpt" />}
        <circle cx={nowX} cy={nowY} r="5.5" className="nowpt" />
        {hov == null && <text x={nearEdge ? nowX - 10 : nowX} y={nowY - 14} textAnchor={nearEdge ? 'end' : 'middle'} className="nowl">{signedXaf(acc.balance)}</text>}
      </svg>
      {h && hov != null && (
        <div className="ag-tip" style={{ left: `${Math.min(86, Math.max(14, (X(hov) / W) * 100))}%` }} role="status">
          <span className="muted">{hov === n - 1 ? S.agency.st.today : day(hov)}</span>
          <b className="num">{signedXaf(hov === n - 1 ? acc.balance : h.balance)}</b>
          {h.topup > 0 && <span className="ag-tipr"><Net p={h.rail ?? 'MTN MoMo'} s={14} />+{xaf(h.topup)}</span>}
          {fee(hov) ? <span className="ag-tipr muted">{S.agency.st.fee} −{xaf(fee(hov)!)}</span> : null}
        </div>
      )}
    </div>
  );
}

/** One short line for the headline: when the balance reaches zero, or that it holds, or since when it is below. */
function headline(acc: AccountSummary) {
  if (acc.paused) return S.ag.acct.hPaused;
  if (acc.balance < 0) return acc.below_zero_since ? S.ag.acct.hBelow(longDay(acc.below_zero_since)) : S.ag.acct.hBelowNow;
  if (acc.fee_per_day <= 0 || acc.days_to_zero == null) return S.ag.acct.hStill;
  return acc.days_to_zero > 45 ? S.ag.acct.hHolds(runwayWords(acc.days_to_zero)) : S.agency.st.zero(longDay(new Date(Date.now() + acc.days_to_zero * 86400000).toISOString()));
}

export function AccountView({ st, acc, side, admin, updatedAt }: {
  st: Statement; acc: AccountSummary; side: React.ReactNode; admin?: { limit: number }; updatedAt?: Date | null;
}) {
  const perDel = st.fees7_n ? Math.round(st.fees7 / st.fees7_n) : null;
  const before = daysUntil(acc.topup_before);
  const urgent = before != null && before <= 45;
  const feesDays = st.days.slice(-7).map((d, i, xs) => {
    const k = st.days.length - xs.length + i;
    return k > 0 ? Math.max(0, st.days[k - 1].balance + d.topup - d.balance) : 0;
  });
  const csv = () => {
    const blob = new Blob([statementCsv(st.lines)], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob); a.download = `releve-${acc.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };
  return (
    <div className="ag-ac5">
      <div className="ag-main">
        <div className="card ag-bigc">
          <div className="ag-row">
            <span className="cap">{S.agency.st.title}</span>
            <span className="ag-hr">{updatedAt && <span className="ag-live" title={S.ag.acct.live}><i />{S.ag.acct.upd(hm(updatedAt))}</span>}<AccountPill a={acc} /></span>
          </div>
          <div className="ag-bal num">{signedXaf(acc.balance).replace(' XAF', '')}<small>XAF</small></div>
          <p className="ag-head"><b>{headline(acc)}</b>{!admin && acc.balance <= 0 && acc.topup_before ? <span className="muted"> · {S.agency.rule2neg(longDay(acc.topup_before))}</span> : null}</p>
          <Curve st={st} acc={acc} limit={admin?.limit} />
          <div className="ag-facts3">
            <div><span className="cap">{S.ag.acct.fPerDay}</span><b className="num">{acc.fee_per_day ? xaf(acc.fee_per_day) : '—'}</b></div>
            <div><span className="cap">{S.ag.acct.fRunway}</span><b>{acc.balance <= 0 ? S.ag.acct.none : acc.days_to_zero == null ? '—' : acc.days_to_zero > 45 ? runwayWords(acc.days_to_zero) : dayShort(acc.days_to_zero)}</b></div>
            <div><span className="cap">{admin ? S.ag.acct.fPause : S.ag.acct.fBefore}</span><b className={urgent ? 'ag-warnv' : ''}>{urgent ? dayShort(before!) : S.ag.acct.notUrgent}</b></div>
          </div>
        </div>
        <div className="card ag-k3">
          <div><span className="ag-kic"><Ic n="moto" s="xs" /></span><span className="cap">{S.agency.st.fees7}</span><b className="num">{xaf(st.fees7)}</b>
            <div className="ag-k3f"><span className="muted">{S.agency.st.fees7Sub(st.fees7_n)}</span><MiniBars vals={feesDays} labels={last7()} /></div></div>
          <div><span className="ag-kic"><Ic n="wallet" s="xs" /></span><span className="cap">{S.agency.st.perDel}</span><b className="num">{perDel != null ? xaf(perDel) : '—'}</b><p className="muted">{S.agency.st.perDelSub(acc.fee_pct)}</p></div>
          <div><span className="ag-kic"><Ic n="up" s="xs" /></span><span className="cap">{S.agency.st.tops30}</span><b className="num">{xaf(st.topups30)}</b><p className="muted">{S.agency.st.tops30Sub(st.topups30_n, st.waiting.length)}</p></div>
        </div>
        <div className="card ag-led">
          <div className="ag-ledh"><h3>{S.agency.st.statement}</h3><button className="btn sm" onClick={csv} disabled={!st.lines.length}><Ic n="download" s="xs" />{S.agency.st.csv}</button></div>
          <div className="tblwrap">
            <table className="tbl ag-lr">
              <thead><tr><th>{S.agency.st.h.date}</th><th aria-hidden="true" /><th>{S.agency.st.h.line}</th><th className="r">{S.agency.st.h.amount}</th><th className="r">{S.agency.st.h.after}</th></tr></thead>
              <tbody>
                {st.waiting.map((w) => (
                  <tr key={w.id} className="pend"><td className="muted">{dayLabel(w.at, { weekday: 'short', day: 'numeric', month: 'short' })} · {hhmm(w.at)}</td><td><Net p={w.provider} s={30} /></td>
                    <td><b>{S.agency.st.topup(netName(w.provider))}</b><small className="muted">{S.agency.st.pend}</small></td><td className="r num ag-pendamt">+{xaf(w.amount)}</td><td className="r muted">{S.agency.st.waitingAmt}</td></tr>
                ))}
                {st.lines.map((l) => (
                  <tr key={l.id}><td className="muted">{dayLabel(l.at, { weekday: 'short', day: 'numeric', month: 'short' })} · {hhmm(l.at)}</td>
                    <td>{l.kind === 'topup' ? <Net p={l.rail ?? 'MTN MoMo'} s={30} /> : <span className="ag-k"><Ic n={l.kind === 'fee' ? 'moto' : 'doc'} s="xs" /></span>}</td>
                    <td><b>{l.kind === 'fee' ? S.agency.st.fee : l.kind === 'topup' ? S.agency.st.topup(netName(l.rail ?? 'MTN MoMo')) : S.agency.st.adj}</b>
                      <small className="muted">{l.kind === 'fee' ? [l.order_ref, S.agency.st.fare(l.quartier, l.fare ? xaf(l.fare) : null)].filter(Boolean).join(' · ') : l.note ?? ''}</small></td>
                    <td className={'r num' + (l.amount > 0 ? ' ag-plus' : '')}>{l.amount > 0 ? '+' : ''}{signedXaf(l.amount)}</td><td className="r num">{signedXaf(l.balance_after)}</td></tr>
                ))}
                {!st.lines.length && !st.waiting.length && <tr><td colSpan={5}><div className="empty">{S.agency.st.none}</div></td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="ag-sidecol">{side}</div>
    </div>
  );
}
